<script setup lang="ts">
import { computed, inject, nextTick, onMounted, onUnmounted, reactive, Ref, ref } from 'vue';
import { useFonts,  type UserFont } from '@/composables/fonts';
import store from '@/store';
import CSelect, { type CSelectItem, type Primitive } from '@/components/shared/CSelect';
import type { AxiosError } from 'axios';
import type { TextCharacterProvider } from './types';
import CButton from '@/components/shared/CButton';
import CFileInput from '@/components/shared/CFileInput';
import ConfirmationModal from '@/components/ConfirmationModal/ConfirmationModal.vue';
import fontManagerService, { DEFAULT_FONT } from '@/utils/fontManagerService';
import { deleteFont } from '@/api/fonts';
import opentype from '@customily/opentype.js';
import { useToaster } from '@/composables/toaster';

const toaster = useToaster();

const CONFLICT_CODE = 409;
const generateSafeFontName = fontManagerService.generateSafeFontName;

interface ConflictErrorResponse {
    type: string;
    errorText: string;
    list: Record<'id' | 'name', string>[];
}

const props = withDefaults(defineProps<{
    value?: CSelectItem<string>;
    label?: string;
    open?: boolean;
    disabled?: boolean;
    appendToBody?: boolean
    showFontUpload?: boolean;
}>(), {
    label: 'Select a font',
    appendToBody: false,
    showFontUpload: true,
});

const emit = defineEmits<{
    (e: 'input', value: CSelectItem<Primitive>): void;
}>();

const panelProvider = inject<TextCharacterProvider>('panelUtils');

const { uploadUserFont, generateFontName } = useFonts();

const fonts = computed(() =>
    store.state.userFonts.map<CSelectItem<string, UserFont>>((font) => ({
        label: generateFontName(font),
        value: font.Path,
        name: font.Name,
        deletable: !!font.User_Id && font.Path.indexOf(DEFAULT_FONT) == -1,
        object: font
    }))
);

const onOptionSelected = (option?: CSelectItem<Primitive> | Primitive) => {
    emit('input', option as CSelectItem<Primitive>);
};

const isSupported = async (file: File) => {
    const buffer = await file.arrayBuffer();
    const font = opentype.parse(buffer);
    if (!font) {
        throw new Error('The font file is not supported.');
    }
};

const onFontUpload = async ([fontFile]: File[]) => {
    try {
        panelProvider?.showLoader();
        await isSupported(fontFile);
        await uploadUserFont(fontFile);
        await refreshFontList();
    } catch (error) {
        toaster.error(`Error uploading font ${fontFile.name}:  ${(error as Error).message}`);
    } finally {
        panelProvider?.showLoader(false);
    }
};

const modal = reactive({
    title: '',
    description: '',
    actionVerb: '',
    show: false,
    hideCancel: true,
    conflictError: undefined as ConflictErrorResponse | undefined,
    action: null as (() => void) | null
});

const confirmFontDelete = (font: CSelectItem) => {
    modal.conflictError = undefined;
    modal.title = 'Confirmation';
    modal.description = `Are you sure you want to delete the font ${font.label}?`
    modal.action = () => onFontDeleted(font);
    modal.actionVerb = 'Ok';
    modal.hideCancel = false;
    modal.show = true;
}

const onFontDeleted = async (font: CSelectItem) => {
    try {
        panelProvider?.showLoader();
        modal.show = false
        const filename = font.value.substring(font.value.lastIndexOf('/') + 1, font.value.length);
        await deleteFont(filename);
        panelProvider?.fontFileDeleted(font.value);
        await refreshFontList();
    } catch (error) {
        const _error = error as AxiosError;
        modal.title = 'Error deleting the font';
        modal.action = () => toggleModal();
        modal.description = _error.response?.data;
        modal.actionVerb = 'Ok';
        modal.hideCancel = true;
        if (_error.response?.status == CONFLICT_CODE) {
            modal.title = 'Font in use!';
            modal.conflictError = _error.response?.data;
        }
        modal.show = true;
    } finally {
        panelProvider?.showLoader(false);
    }
};

const toggleModal = () => {
    modal.show = !modal.show
};

const searchString = ref('');
const filteredFonts = computed(() =>
    fonts.value.filter((font) => font.label.toLowerCase().includes(searchString.value.toLowerCase()))
);
const fontList: Ref<CSelectItem[]> = ref([]);

const observer = new IntersectionObserver(async ([entry]) => {
    if (!entry.isIntersecting) return;

    const ul = (entry.target as HTMLElement).offsetParent!;
    const scrollTop = ul.scrollTop;

    loadFonts(fontList.value.length - 1 + fontManagerService.maxFontsToDownload);

    await nextTick();
    ul.scrollTop = scrollTop;
});

const loadFonts = async (limit = fontManagerService.maxFontsToDownload) => {
    const startIdx = searchString.value.length ? 0 : fontList.value.length;
    const newFonts = filteredFonts.value.slice(startIdx, limit).map(f => f.object!);
    fontList.value = filteredFonts.value.slice(0, limit);
    await fontManagerService.loadFontChunksToDOM(newFonts);
};

const refreshFontList = async () => {
    await fontManagerService.initFonts(true);
    loadFonts();
}

const selectorBottom: Ref<HTMLElement | null> = ref(null);
const dropOpen = async () => {
    await nextTick();
    if (!selectorBottom.value) return
    observer.observe(selectorBottom.value)
};

const dropClosed = () => {
    observer.disconnect();
};

const onFontSearch = (searchValue: string) => {
    searchString.value = searchValue || '';
    loadFonts();
}

onMounted(() => {
    loadFonts();
});

onUnmounted(() => {
    dropClosed();
});
</script>

<template>
    <div class="d-flex align-items-end">
        <confirmation-modal
            :value="modal.show"
            :title="modal.title"
            :action-verb="modal.actionVerb"
            :hide-cancel="!!modal.conflictError || modal.hideCancel"
            @ok="modal.action?.()"
            @cancel="toggleModal"
        >
            <template #modal-content>
                <div v-if="!modal.conflictError" v-html="modal.description" />
                <div v-else>
                    <p class="font-weight-bold">{{ modal.conflictError.errorText }}</p>
                    <ul>
                        <li v-for="item in modal.conflictError.list" :key="item.id">
                            <strong>Id:</strong> {{ item.id }}<br />
                            <strong>Name:</strong> {{ item.name }}
                        </li>
                    </ul>
                </div>
            </template>
        </confirmation-modal>
        <c-select
            class="flex-fill"
            :label="label"
            :value="props.value"
            searchable
            :options="fontList"
            :always-open="props.open"
            :disabled="disabled"
            :filterable="false"
            :append-to-body="appendToBody"
            @input="onOptionSelected($event)"
            @open="dropOpen"
            @close="dropClosed"
            @search="onFontSearch"
        >
            <template #selected-option="{ label }">
                <span :style="{ fontFamily: `'${generateSafeFontName(label)}', Arial` }">{{ label }}</span>
            </template>
            <template #option="option">
                <div class="d-flex justify-content-between align-items-center">
                    <span :style="{ fontFamily: `'${generateSafeFontName(option.label)}', Arial` }">{{
                        option.label
                    }}</span>
                    <c-button
                        v-if="option.deletable"
                        class="ml-2"
                        size="icon"
                        :icon="['far', 'trash-can']"
                        @click.stop="confirmFontDelete(option)"
                    />
                </div>
            </template>
            <template #list-footer>
                <li
                    ref="selectorBottom"
                    :class="filteredFonts.length == fontList.length ? 'd-none' : 'd-flex'"
                    class="p-2 align-items-center"
                >
                    <font-awesome-icon class="mr-2 fa-spin" :icon="['fas', 'spinner-third']" />
                    Loading fonts...
                </li>
            </template>
        </c-select>
        <c-file-input
            v-if="showFontUpload"
            :value="[]"
            accept=".ttf,.otf"
            :display-preview="false"
            :disabled="disabled"
            @input="onFontUpload"
        >
            <template #uploadTrigger="{ onFileUpload, disabled: triggerDisabled }">
                <c-button
                    class="ml-2"
                    variant="secondary"
                    :icon="['far', 'arrow-up-from-bracket']"
                    :disabled="triggerDisabled"
                    @click="onFileUpload"
                />
            </template>
        </c-file-input>
    </div>
</template>

<style scoped></style>
